"use client";

import { motion } from "framer-motion";
import { Users, FileSpreadsheet, MailX, AlertTriangle } from "lucide-react";
import { fadeUp, Section } from "./utils";

export function ProblemSection() {
  const painPoints = [
    {
      icon: Users,
      title: "You're the bottleneck in your own business",
      copy: "Every decision, every handoff, every follow-up runs through you. You hired people to help — but you still spend your day coordinating instead of growing.",
      stat: "Founders spend 60% of their time on coordination",
    },
    {
      icon: FileSpreadsheet,
      title: "Your operations live in 4 different tools",
      copy: "Messages in one app, notes in a spreadsheet, tasks in your head. Nothing talks to anything, so work stalls and details get lost.",
      stat: "3-4 hours/week lost to manual tracking",
    },
    {
      icon: MailX,
      title: "Things fall through the cracks when you're not watching",
      copy: "The meeting went well. The client said \"let me think about it.\" Nobody followed up. Three days later, the deal went cold — and nobody noticed.",
      stat: "44% of deals go cold from missed follow-ups",
    },
  ];

  return (
    <Section className="px-6 py-28 max-w-7xl mx-auto">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-sm font-medium text-coral-400 mb-3 uppercase tracking-wider">
          Sound familiar?
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-content">
          You&apos;re running the business
          <br className="hidden sm:block" />
          <span className="text-content-muted"> and doing everyone&apos;s job too.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {painPoints.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={i}
              variants={fadeUp}
              custom={i}
              className="bg-surface/60 border border-surface-border rounded-2xl p-8 hover:border-danger/30 transition-all duration-300 flex flex-col"
            >
              <div className="w-12 h-12 rounded-xl bg-danger/10 border border-danger/20 flex items-center justify-center mb-5">
                <Icon size={22} className="text-danger" />
              </div>
              <h3 className="text-lg font-semibold text-content mb-3">
                {p.title}
              </h3>
              <p className="text-sm text-content-muted leading-relaxed mb-4 flex-1">
                {p.copy}
              </p>
              <p className="text-xs font-medium text-danger/80 flex items-center gap-1.5">
                <AlertTriangle size={12} />
                {p.stat}
              </p>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}
